import {
  faTriangleExclamation,
  faCircleExclamation,
  faWifiSlash,
  faServer,
  faMobileSignal,
} from '@fortawesome/pro-solid-svg-icons';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { HoruzProtocolChulakCloseCode } from '@ver.id/libs.internal.horuz';
import { type AbortVariant } from '../types';

export type AbortVariantConfig = {
  icon: IconDefinition;
  iconClass: string;
  bgClass: string;
  titleKey: string;
  subtitleKey: string;
  showRestart: boolean;
  restartKey: string;
};

/** The code the website hears in `verid:error` for a variant: `serverError` → `server_error`. */
export function errorCode(variant: AbortVariant): string {
  return variant.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

export function getAbortVariant(errorCode?: number): AbortVariant {
  switch (errorCode) {
    case HoruzProtocolChulakCloseCode.AGENT_CANCEL:
    case HoruzProtocolChulakCloseCode.APP_CANCEL:
      return 'cancelled';
    case HoruzProtocolChulakCloseCode.APP_TIMEOUT:
    case HoruzProtocolChulakCloseCode.PROTOCOL_AUTHENTICATION_TIMEOUT_ERROR:
    case HoruzProtocolChulakCloseCode.PROTOCOL_AUTHENTICATION_ERROR:
      return 'expired';
    case HoruzProtocolChulakCloseCode.AGENT_DISCONNECT:
      return 'connectionLost';
    case HoruzProtocolChulakCloseCode.APP_ERROR:
    case HoruzProtocolChulakCloseCode.APP_INVALID_RESPONSE:
      return 'appError';
    default:
      return 'serverError';
  }
}

/**
 * The website's own setup, refused: a malformed init or request, a scope or
 * client the flow does not allow, an origin it does not embed on. The person
 * sees a generic error that sends them to the organisation, never blaming
 * its website; the page hears the precise code.
 */
const CONTACT_ORGANISATION: AbortVariantConfig = {
  icon: faCircleExclamation,
  iconClass: 'text-destructive',
  bgClass: 'bg-red-100',
  titleKey: 'abort.serverError.title',
  subtitleKey: 'abort.contactOrganisation.subtitle',
  showRestart: false,
  restartKey: 'abort.restart',
};

export const abortVariantConfig: Record<AbortVariant, AbortVariantConfig> = {
  cancelled: {
    icon: faTriangleExclamation,
    iconClass: 'text-amber-600',
    bgClass: 'bg-amber-100',
    titleKey: 'abort.cancelled.title',
    subtitleKey: 'abort.cancelled.subtitle',
    showRestart: true,
    restartKey: 'abort.restart',
  },
  serverError: {
    icon: faCircleExclamation,
    iconClass: 'text-destructive',
    bgClass: 'bg-red-100',
    titleKey: 'abort.serverError.title',
    subtitleKey: 'abort.serverError.subtitle',
    showRestart: true,
    restartKey: 'abort.restart',
  },
  invalidRequest: CONTACT_ORGANISATION,
  invalidScope: CONTACT_ORGANISATION,
  expired: {
    icon: faClock,
    iconClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
    titleKey: 'abort.expired.title',
    subtitleKey: 'abort.expired.subtitle',
    showRestart: false,
    restartKey: 'abort.restart',
  },
  connectionLost: {
    icon: faWifiSlash,
    iconClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
    titleKey: 'abort.connectionLost.title',
    subtitleKey: 'abort.connectionLost.subtitle',
    showRestart: true,
    restartKey: 'abort.retry',
  },
  temporarilyUnavailable: {
    icon: faServer,
    iconClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
    titleKey: 'abort.temporarilyUnavailable.title',
    subtitleKey: 'abort.temporarilyUnavailable.subtitle',
    showRestart: true,
    restartKey: 'abort.retry',
  },
  appError: {
    icon: faMobileSignal,
    iconClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
    titleKey: 'abort.appError.title',
    subtitleKey: 'abort.appError.subtitle',
    showRestart: false,
    restartKey: 'abort.retry',
  },
  unauthorizedClient: CONTACT_ORGANISATION,
  originNotAllowed: CONTACT_ORGANISATION,
};
